import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Stack,
  Box,
  TextField,
  FormControl,
  FormLabel,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { isEmpty } from 'lodash';

import { AppContext } from 'views/App/context';
import { translate as t } from 'hooks/useTranslations';
import { useQueryClient } from 'react-query';
import useManageManual from '../_data_sources/useManageManual';
import useDraftManual from '../_data_sources/useDraftManual';
import useJurisdictions from 'data_sources/useJurisdictions';
import useVerticals from 'data_sources/useVerticals';
import useManualTypes from 'data_sources/useManualTypes';
import { Form } from 'components/_forms';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';
import { enqueueSnackbar } from 'notistack';
import JurisdictionPicker from 'components/_pickers/JurisdictionPicker';
import { CreateJurisdictionManualRequest } from '../_data_sources/useManageManual';

const CreateJurisdictionManual: React.FC = () => {
  const navigate = useNavigate();
  const { id: globalManualId } = useParams<{ id: string }>();
  const { currentOrganizationId: orgId, isLexipolStaff } = useContext(AppContext);

  const [jurisdictionId, setJurisdictionId] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();

  const queryClient = useQueryClient();
  const { createJurisdictionManual } = useManageManual();
  const globalManualQuery = useDraftManual(globalManualId);
  const jurisdictionsQuery = useJurisdictions();
  const verticalsQuery = useVerticals();
  const manualTypesQuery = useManualTypes();

  // Get the global manual data
  const globalManual = globalManualQuery.data;
  const verticalId = globalManual?.verticalId;
  const manualTypeId = globalManual?.manualTypeId;

  // Get the vertical and manual type names for display
  const vertical = useMemo(() => {
    return verticalsQuery.data?.find((v) => v.id === verticalId);
  }, [verticalsQuery.data, verticalId]);

  const manualType = useMemo(() => {
    return manualTypesQuery.data?.find((mt) => mt.id === manualTypeId);
  }, [manualTypesQuery.data, manualTypeId]);

  const jurisdiction = useMemo(() => {
    return jurisdictionsQuery.data?.find((j) => j.id === jurisdictionId);
  }, [jurisdictionsQuery.data, jurisdictionId]);

  // Generate title based on selections
  const generatedTitle = useMemo(() => {
    if (!jurisdictionId || !vertical || !manualType) return '';

    const jurisdictionName = jurisdiction?.name || jurisdictionId;
    const verticalName = vertical.name || verticalId;
    const manualTypeName = manualType.name || manualTypeId;

    return `${jurisdictionName} ${verticalName} ${manualTypeName} Manual`;
  }, [jurisdictionId, vertical, manualType, jurisdiction, verticalId, manualTypeId]);

  // Redirect if not Lexipol staff
  useEffect(() => {
    if (!isLexipolStaff) {
      navigate('..');
    }
  }, [isLexipolStaff, navigate]);

  // Redirect if global manual not found (after loading is complete)
  useEffect(() => {
    if (!globalManualQuery.isLoading && !globalManual) {
      navigate('..');
    }
  }, [globalManualQuery.isLoading, globalManual, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!jurisdictionId || !verticalId || !manualTypeId) {
      return;
    }

    const payload: CreateJurisdictionManualRequest = {
      manualLevel: 'JURISDICTION',
      manualTypeId,
      verticalId,
      jurisdictionId,
      globalManualId: globalManualId || '',
    };

    setIsCreating(true);
    setServerErrors(undefined);

    createJurisdictionManual(
      orgId || '',
      payload,
      async (response) => {
        // Show success toast
        enqueueSnackbar(t('draftManuals.createJurisdictionManual.success', '', { manualName: generatedTitle }), {
          variant: 'success',
        });

        // Wait for the query invalidation to complete before navigating
        await queryClient.invalidateQueries(['draft_manuals']);

        // Navigate to the newly created manual
        navigate(`..`);
      },
      (error) => {
        setServerErrors(error);
        setIsCreating(false);
      }
    );
  };

  // Return null if not Lexipol staff or if global manual not found
  if (!isLexipolStaff || (!globalManualQuery.isLoading && !globalManual)) {
    return null;
  }

  // Show loading while fetching global manual data
  if (globalManualQuery.isLoading) {
    return (
      <TakeoverLayout
        pageTitle={t('draftManuals.createJurisdictionManual.title')}
        backUrl='..'
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </TakeoverLayout>
    );
  }

  return (
    <TakeoverLayout
      pageTitle={t('draftManuals.createJurisdictionManual.title')}
      backUrl='..'
    >
      <Box>
        <Card
          sx={{ maxWidth: 800, mx: 'auto', width: '100%' }}
          className='lex-form'
        >
          <Form onSubmit={handleSubmit}>
            <CardContent>
              {errorHelper.renderAllErrors(serverErrors)}
              <Stack spacing={4}>
                {/* Jurisdiction Field - Required dropdown */}
                <JurisdictionPicker
                  id='jurisdiction-picker'
                  label={t('draftManuals.createJurisdictionManual.form.jurisdiction')}
                  value={jurisdictionId ? [jurisdictionId] : []}
                  onChange={(val) => setJurisdictionId(val[0] || '')}
                  placeholder={t('draftManuals.createJurisdictionManual.form.jurisdictionPlaceholder')}
                  required
                  disabled={jurisdictionsQuery.isLoading}
                />

                {/* Vertical Field - Disabled and auto-filled */}
                <TextField
                  label={t('draftManuals.createJurisdictionManual.form.vertical')}
                  value={vertical?.name || verticalId || ''}
                  disabled
                  fullWidth
                />

                {/* Manual Type Field - Disabled and auto-filled */}
                <TextField
                  label={t('draftManuals.createJurisdictionManual.form.manualType')}
                  value={manualType?.name || manualTypeId || ''}
                  disabled
                  fullWidth
                />

                {/* Title Field - Disabled and auto-generated */}
                <TextField
                  label={t('draftManuals.createJurisdictionManual.form.title')}
                  value={generatedTitle}
                  disabled
                  fullWidth
                  helperText={t('draftManuals.createJurisdictionManual.form.titleHelper')}
                />
              </Stack>
            </CardContent>
            <CardActions>
              <Box />
              <Stack
                direction='row'
                spacing={2}
              >
                <Button
                  color='neutral'
                  href='..'
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  disabled={isCreating || isEmpty(jurisdictionId)}
                  component='button'
                  type='submit'
                  variant='contained'
                  endIcon={
                    isCreating ? (
                      <CircularProgress
                        size={20}
                        color='inherit'
                      />
                    ) : undefined
                  }
                >
                  {t('draftManuals.createJurisdictionManual.form.create')}
                </Button>
              </Stack>
            </CardActions>
          </Form>
        </Card>
      </Box>
    </TakeoverLayout>
  );
};

export default CreateJurisdictionManual;
